import { useCallback, useContext, useEffect, useRef } from 'react';
import GlobalContext from '~/context/vdp/GlobalContext';
import scrollToElement from '~/utilities/vdp/scrollToElement';
import useRepositionScheduler from './useRepositionScheduler';

/**
 * Scrolls to a section by ID, disabling lazy loading first so the target
 * element renders. Schedules staggered repositions to compensate for
 * page height changes as content loads asynchronously.
 *
 * Repositions are cancelled when the user scrolls or a different
 * hash change occurs.
 */
export default function useScrollToSection() {
  const { isLazyLoadEnabled, setLazyLoadEnabled } = useContext(GlobalContext);
  const { clearRepositions, scheduleRepositions } = useRepositionScheduler();

  const isOwnHashChangeRef = useRef(false);

  // Cancel repositioning when user scrolls or an external hash change occurs
  useEffect(() => {
    const handleUserScroll = () => clearRepositions();

    const handleHashChange = () => {
      if (isOwnHashChangeRef.current) {
        isOwnHashChangeRef.current = false;

        return;
      }
      clearRepositions();
    };

    window.addEventListener('wheel', handleUserScroll, { passive: true });
    window.addEventListener('touchmove', handleUserScroll, { passive: true });
    window.addEventListener('hashchange', handleHashChange);

    return () => {
      window.removeEventListener('wheel', handleUserScroll);
      window.removeEventListener('touchmove', handleUserScroll);
      window.removeEventListener('hashchange', handleHashChange);
      clearRepositions();
    };
  }, [clearRepositions]);

  const scrollToSection = useCallback(
    (sectionId: string) => {
      clearRepositions();

      if (isLazyLoadEnabled) {
        setLazyLoadEnabled(false);
      }

      isOwnHashChangeRef.current = true;
      window.history.pushState(null, '', `#${sectionId}`);
      window.dispatchEvent(new HashChangeEvent('hashchange'));
      scrollToElement(sectionId);
      scheduleRepositions(sectionId);
    },
    [
      clearRepositions,
      isLazyLoadEnabled,
      scheduleRepositions,
      setLazyLoadEnabled
    ]
  );

  return scrollToSection;
}
